import { CreateAttachmentDependencies } from './createAttachment';
import handleCreateAttachmentNotification from './handleCreateAttachmentNotification';
import { onAttachmentCreationError } from './helpers/onAttachmentCreationHandlers';
import prepareNotificationAndExecuteSendRequest from './helpers/prepareNotificationAndExecuteSendRequest';
import datapoints from '../datapoints';
import createReferenceAttachmentFromLocalFileService from '../services/createReferenceAttachmentFromLocalFile';
import { ClientItemId, ClientAttachmentId } from 'owa-client-ids';
import { NotificationSubscription } from 'owa-notification';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import NotificationPayloadBase from 'owa-service/lib/contract/NotificationPayloadBase';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import {
    fetchUploadFolder,
    getUploadFolder,
    UploadFolderMailboxType,
} from 'owa-attachments-upload-folder';
import {
    AttachmentFullViewState,
    AttachmentState,
    getShareLocalFileCompleteTimeInMS,
    setActionCompletePercent,
    startFakeProgressIndicator,
    stopFakeProgressIndicator,
} from 'owa-attachment-full-data';
const MAX_SLICE_SIZE = 10 * 1024 * 1024;

export default createLegacyActionWithDatapoint(
    'createReferenceAttachmentFromLocalFile',
    datapoints.AttachmentCreationCloudyFromLocalFile
)(function createReferenceAttachmentFromLocalFile(
    parentItemId: ClientItemId,
    attachmentId: ClientAttachmentId,
    attachment: AttachmentType,
    cancellationId: string,
    backingObject: Blob,
    attachmentState: AttachmentState,
    onAttachmentCreated: (attachment: AttachmentType) => void,
    onAttachmentCreationFailed: () => void,
    dependencies: CreateAttachmentDependencies
) {
    const handleNotification = (notification: NotificationPayloadBase) => {
        handleCreateAttachmentNotification(
            notification,
            parentItemId,
            attachmentId,
            attachment,
            attachmentState,
            onAttachmentCreated,
            onAttachmentCreationFailed,
            dependencies
        );
    };

    const sendRequest = (
        subscriptionId: string,
        subscription: NotificationSubscription,
        channelId: string,
        notificationHandler: (notification: NotificationPayloadBase) => void
    ) => {
        internalSendRequest(
            parentItemId,
            attachment,
            cancellationId,
            backingObject,
            attachmentState,
            subscriptionId,
            subscription,
            channelId,
            notificationHandler,
            MAX_SLICE_SIZE,
            onAttachmentCreationFailed,
            dependencies
        );
    };

    prepareNotificationAndExecuteSendRequest(sendRequest, handleNotification);
});

// Exporting for testing purposes
export async function internalSendRequest(
    parentItemId: ClientItemId,
    attachment: AttachmentType,
    cancellationId: string,
    backingObject: Blob,
    attachmentState: AttachmentState,
    subscriptionId: string,
    subscription: NotificationSubscription,
    channelId: string,
    notificationHandler: (notification: NotificationPayloadBase) => void,
    maxSliceSize: number, // Adding this param here for testing purposes
    onAttachmentCreationFailed: () => void,
    dependencies: CreateAttachmentDependencies
): Promise<void> {
    const totalNumberOfSlices = Math.ceil(backingObject.size / maxSliceSize);

    startFakeProgressIndicator(
        getShareLocalFileCompleteTimeInMS(backingObject.size),
        0.05,
        (completePercent: number) => {
            setActionCompletePercent(<AttachmentFullViewState>attachmentState, completePercent);
        },
        attachmentState.attachmentId
    );

    // Get the folderId to upload to if the compose form is opened in a group
    let folderId = null;
    const groupId = dependencies?.attachmentWellViewState?.groupId;
    if (groupId) {
        folderId = getUploadFolder(groupId) ? getUploadFolder(groupId).folderId : null;
        if (!folderId) {
            const response = await fetchUploadFolder(groupId, UploadFolderMailboxType.Group);
            if (response) {
                folderId = response.folderId;
            }
        }
    }

    for (let i = 0; i < totalNumberOfSlices; i++) {
        const blobStart = 0 + i * maxSliceSize;
        const blobEnd = Math.min(blobStart + maxSliceSize, backingObject.size);
        const slice: Blob = backingObject.slice(blobStart, blobEnd);

        try {
            const response: string = await createReferenceAttachmentFromLocalFileService(
                parentItemId,
                attachment,
                cancellationId,
                slice,
                subscriptionId,
                channelId,
                i,
                totalNumberOfSlices,
                groupId,
                folderId
            );

            if (!response) {
                throw new Error(
                    'Empty response for createReferenceAttachmentFromLocalFile request'
                );
            }
        } catch (error) {
            stopFakeProgressIndicator(attachmentState.attachmentId, false);
            onAttachmentCreationFailed();
            onAttachmentCreationError(
                attachmentState,
                dependencies.attachmentWellViewState,
                subscription,
                notificationHandler
            );
            return;
        }
    }
}
